/**
 * TrackRow — one track in a list: cover, title, the billing and the year, the duration.
 *
 * Task 05, checklist items 3 and 5. What the row does NOT draw is as deliberate as what it
 * does:
 *
 * - **No performer, no second line.** A track with no known performer shows its title and
 *   nothing where the name would be — no «Unknown Artist», no «—» (`spec.md` «Каталог и
 *   данные»). The year alone takes the line when there is one.
 * - **No year, no year.** `year` is `null` unless the owner wrote `year:` in the caption.
 * - **No duration, no `0:00`.** A file sent with the paperclip has none; see `lib/format.ts`.
 *
 * ## Two buttons: the row plays, the dots open the card
 *
 * Task 07 made a tap on the row mean «play», because that is what a person tapping a track
 * in a player means. The card — the billing in full, the riddim and the tags as buttons of
 * their own — moved to a second button at the row's edge, «О треке». Before task 07 the whole
 * row opened the card, and leaving it so would have made the card the only way to hear
 * anything; making the row play without a second button would have made the tags
 * unreachable (the debt «Нажатие строки занято карточкой» of task 05).
 *
 * The row that is playing says so: `aria-current`, the gold title and — since task 67 — a gold
 * bar at its left edge with a gold glow, and four green bars before the time, like a level meter,
 * drawn for the eye only. The bars jump while the sound plays and stand still on a pause
 * (`data-playing`) and under «Уменьшение движения». The row reads two booleans from the player
 * slice, so the four position updates a second re-render no row.
 *
 * ## A third button: the heart (task 11)
 *
 * Between the two, `components/FavoriteButton.tsx`: «в избранное» one tap away on every row,
 * which is what the checklist asks. It sits beside «О треке», not inside the row button — a
 * button inside a button is not a button to a screen reader — and the title is what gives way
 * for it: a long one ends in an ellipsis 44 pixels sooner.
 *
 * {@link ROW_HEIGHT} is the height the virtual list lays the row out with, and it must equal
 * `--row-h` in `styles/tokens.css`. The two are held equal by a test rather than read from
 * each other: a CSS custom property read at runtime is an empty string in every environment
 * without layout, and a virtual list that believes its rows are zero pixels tall renders all
 * of them.
 */

import type { JSX } from 'react';
import { Fragment, memo } from 'react';

import type { TrackItem } from '../lib/api';
import { formatDuration, trackByline } from '../lib/format';
import { markMatches } from '../lib/highlight';
import { usePlayer } from '../store/player';

import { Cover } from './Cover';
import { FavoriteButton } from './FavoriteButton';

/** Pixels. Equal to `--row-h`. */
export const ROW_HEIGHT = 74;

/** Task 67: the level meter of the row that is playing — four green bars, for the eye only. */
export function LevelBars({ moving }: { readonly moving: boolean }): JSX.Element {
  return (
    <span className="rowEq" data-playing={moving ? 'true' : 'false'} aria-hidden="true">
      <span className="rowEqBar" />
      <span className="rowEqBar" />
      <span className="rowEqBar" />
      <span className="rowEqBar" />
    </span>
  );
}

/** The accessible name of the second button of a row. */
export function detailsLabel(title: string): string {
  return `О треке «${title}»`;
}

export interface TrackRowProps {
  readonly track: TrackItem;
  readonly onPlay: (track: TrackItem) => void;
  readonly onDetails: (track: TrackItem) => void;
  /** Task 54: the row the pult's screen chose for LOAD, lit. */
  readonly picked?: boolean | undefined;
  /** Task 72: a search's query — where it stands in the title and the artist, in gold. */
  readonly mark?: string | undefined;
}

/** Task 72: `text` with the places `mark` stands in it marked (`lib/highlight.ts`). */
function Marked({
  text,
  mark,
}: {
  readonly text: string;
  readonly mark: string | undefined;
}): JSX.Element {
  if (mark === undefined) return <>{text}</>;
  return (
    <>
      {markMatches(text, mark).map((piece, at) =>
        piece.hit ? (
          <mark key={at} className="searchHit">
            {piece.text}
          </mark>
        ) : (
          <Fragment key={at}>{piece.text}</Fragment>
        ),
      )}
    </>
  );
}

export const TrackRow = memo(function TrackRow({
  track,
  onPlay,
  onDetails,
  picked = false,
  mark,
}: TrackRowProps): JSX.Element {
  const byline = trackByline(track.performer, track.year);
  const duration = formatDuration(track.durationS);
  const current = usePlayer((s) => s.trackId === track.id);
  const moving = usePlayer((s) => s.trackId === track.id && s.status === 'playing');

  return (
    <li
      className="row trackRow"
      data-track-id={track.id}
      data-current={current ? 'true' : undefined}
      data-picked={picked ? 'true' : undefined}
    >
      <button
        type="button"
        className={current ? 'rowButton rowCurrent' : 'rowButton'}
        aria-current={current ? 'true' : undefined}
        onClick={() => {
          onPlay(track);
        }}
      >
        <Cover src={track.cover} className="trackCover" />
        <span className="trackText">
          <span className="trackTitle">
            <Marked text={track.title} mark={mark} />
          </span>
          {byline !== '' && (
            <span className="trackByline">
              <Marked text={byline} mark={mark} />
            </span>
          )}
        </span>
        {current && <LevelBars moving={moving} />}
        {duration !== '' && <span className="trackDuration">{duration}</span>}
      </button>
      <FavoriteButton
        trackId={track.id}
        title={track.title}
        className="rowFav"
        iconClassName="rowFavIcon"
      />
      <button
        type="button"
        className="rowMore"
        aria-haspopup="dialog"
        aria-label={detailsLabel(track.title)}
        onClick={() => {
          onDetails(track);
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" className="rowMoreIcon">
          <circle cx="12" cy="5" r="2" fill="currentColor" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <circle cx="12" cy="19" r="2" fill="currentColor" />
        </svg>
      </button>
    </li>
  );
});
